import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
const DiagnosticTest = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [testCompleted, setTestCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [result, setResult] = useState<{
    percentage: number;
    category: string;
    color: string;
    explanation: string;
    recommendations: string[];
    timeframe: string;
  }>({
    percentage: 0,
    category: '',
    color: '',
    explanation: '',
    recommendations: [],
    timeframe: ''
  });
  const questions = [{
    question: '¿Cuánto tiempo ha pasado desde la ruptura o distanciamiento?',
    options: ['Menos de 1 semana', 'Entre 1 y 4 semanas', 'Entre 1 y 3 meses', 'Más de 3 meses'],
    explanation: 'El tiempo transcurrido es crucial para determinar la estrategia. Períodos muy cortos pueden indicar decisiones emocionales reversibles, mientras que períodos más largos requieren enfoques de reconexión más profundos.',
    impact: 'Alto'
  }, {
    question: '¿Cómo fue la comunicación en las últimas interacciones?',
    options: ['Hostil o bloqueada', 'Fría y distante', 'Educada pero superficial', 'Amigable o con señales positivas'],
    explanation: 'La calidad de las últimas interacciones revela el estado emocional actual y los posibles obstáculos a superar. Una comunicación hostil requerirá un período de enfriamiento antes de cualquier acercamiento.',
    impact: 'Muy alto'
  }, {
    question: '¿Cuánto tiempo duró la relación?',
    options: ['No llegamos a tener una relación oficial', 'Menos de 3 meses', 'Entre 3 meses y 1 año', 'Más de 1 año'],
    explanation: 'La duración de la relación indica la profundidad de los lazos emocionales y recuerdos compartidos que pueden utilizarse como puntos de conexión.',
    impact: 'Medio'
  }, {
    question: '¿Él todavía muestra alguna señal de interés?',
    options: ['Ninguna señal, me ignora completamente', 'Muy raramente responde o interactúa', 'Responde, pero sin entusiasmo', 'Sí, todavía hay señales de interés'],
    explanation: 'Las señales de interés residual son indicadores clave de la receptividad a los esfuerzos de reconexión. Incluso pequeñas señales pueden ser amplificadas con las técnicas correctas.',
    impact: 'Muy alto'
  }, {
    question: '¿Está en otra relación actualmente?',
    options: ['Sí, parece seria y estable', 'Sí, pero parece reciente o casual', 'No estoy segura', 'No, está soltero'],
    explanation: 'El estado relacional actual determina las barreras existentes y la estrategia temporal. Relaciones nuevas suelen ser más vulnerables a la comparación con experiencias pasadas significativas.',
    impact: 'Alto'
  }, {
    question: '¿Qué tan intensa fue la conexión emocional durante la relación?',
    options: ['Superficial, principalmente física', 'Moderada, con algunos momentos significativos', 'Profunda, con fuerte vínculo emocional', 'Muy intensa, con planes de futuro compartidos'],
    explanation: 'La intensidad de la conexión pasada determina la fuerza de los recuerdos emocionales que pueden reactivarse mediante los disparadores adecuados.',
    impact: 'Alto'
  }, {
    question: '¿Cuál fue la causa principal de la ruptura o distanciamiento?',
    options: ['Infidelidad o traición grave', 'Conflictos constantes o incompatibilidad', 'Circunstancias externas (distancia, trabajo, etc.)', 'Malentendidos o problemas de comunicación'],
    explanation: 'La causa de la separación revela los obstáculos específicos a superar y las áreas que requieren reparación o demostración de cambio.',
    impact: 'Muy alto'
  }, {
    question: '¿Cómo reaccionaste después de la ruptura o distanciamiento?',
    options: ['Con desesperación, mensajes o llamadas excesivas', 'Con enojo o acusaciones', 'Con tristeza visible pero controlada', 'Con dignidad y espacio'],
    explanation: 'Tu reacción inicial ha establecido una impresión que ahora deberá ser gestionada. Reacciones negativas requerirán un período más largo de reconstrucción de imagen.',
    impact: 'Medio'
  }, {
    question: '¿Tienen amigos o círculos sociales en común?',
    options: ['No, círculos completamente separados', 'Pocos amigos en común, contacto limitado', 'Varios amigos en común, contacto ocasional', 'Muchos amigos en común, contacto frecuente'],
    explanation: 'Los círculos sociales compartidos ofrecen oportunidades naturales de reconexión y canales para influencia indirecta.',
    impact: 'Medio'
  }, {
    question: '¿Qué tipo de personalidad tiene él?',
    options: ['Muy racional y analítico', 'Reservado y cauteloso con sus emociones', 'Expresivo pero indeciso', 'Apasionado e impulsivo'],
    explanation: 'El tipo de personalidad determina qué disparadores emocionales serán más efectivos y el ritmo óptimo para el acercamiento.',
    impact: 'Alto'
  }];
  const handleAnswerSelect = (questionIndex: number, answerValue: number) => {
    setAnswers({
      ...answers,
      [questionIndex]: answerValue
    });
  };
  const goToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowExplanation(false);
    } else {
      calculateResult();
      setTestCompleted(true);
    }
  };
  const goToPreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowExplanation(false);
    }
  };
  const calculateResult = () => {
    // Calculate weighted score based on question impact
    const weights = {
      0: 1,
      1: 2,
      2: 1,
      3: 2,
      4: 1.5,
      5: 1.5,
      6: 2,
      7: 1,
      8: 1,
      9: 1.5 // High
    };
    let totalPoints = 0;
    let maxPossiblePoints = 0;
    Object.entries(answers).forEach(([questionIdx, answerValue]) => {
      const qIdx = parseInt(questionIdx);
      const weight = weights[qIdx as keyof typeof weights] || 1;
      totalPoints += answerValue * weight;
      maxPossiblePoints += 3 * weight; // 3 is max value per question (0-3)
    });
    const percentage = Math.round(totalPoints / maxPossiblePoints * 100);
    let resultData = {
      percentage,
      category: '',
      color: '',
      explanation: '',
      recommendations: [] as string[],
      timeframe: ''
    };
    if (percentage >= 85) {
      resultData = {
        percentage,
        category: 'Muy Alta',
        color: 'text-green-600',
        explanation: 'Tu situación presenta condiciones ideales para una reconquista exitosa. Las señales indican que existe una fuerte conexión emocional residual y circunstancias favorables para reestablecer la relación con un enfoque estratégico.',
        recommendations: ['Implementa el protocolo completo de 21 días sin omitir ninguna fase', 'Enfócate en disparadores de Nostalgia y Valor para reactivar emociones positivas', 'Mantén un equilibrio perfecto entre disponibilidad y escasez (regla 80/20)', 'Inicia el primer contacto dentro de las próximas 48 horas para aprovechar el momentum emocional'],
        timeframe: '2-3 semanas'
      };
    } else if (percentage >= 70) {
      resultData = {
        percentage,
        category: 'Alta',
        color: 'text-green-500',
        explanation: 'Tu caso presenta buenas posibilidades de reconquista, aunque existen algunos obstáculos que requerirán atención específica. Con la estrategia correcta, las probabilidades están claramente a tu favor.',
        recommendations: ['Sigue el protocolo de 21 días con énfasis especial en la fase de Reconexión', 'Utiliza una combinación equilibrada de disparadores de Escasez y Nostalgia', 'Trabaja en tu desarrollo personal para proyectar una imagen renovada', 'Considera un período inicial de 5-7 días de silencio estratégico antes del primer contacto'],
        timeframe: '3-5 semanas'
      };
    } else if (percentage >= 50) {
      resultData = {
        percentage,
        category: 'Media',
        color: 'text-yellow-500',
        explanation: 'Tu situación presenta desafíos significativos, pero la reconquista sigue siendo viable con un enfoque paciente y estratégico. Será necesario superar obstáculos específicos antes de poder avanzar hacia la reconexión emocional.',
        recommendations: ['Implementa una versión extendida del protocolo (30-45 días)', 'Comienza con un período de silencio estratégico de 10-14 días', 'Enfócate intensamente en tu transformación personal antes de iniciar contacto', 'Utiliza principalmente disparadores de Valor y Vulnerabilidad para romper barreras emocionales', 'Considera la posibilidad de una "reconexión accidental" en entornos sociales compartidos'],
        timeframe: '6-8 semanas'
      };
    } else {
      resultData = {
        percentage,
        category: 'Desafiante',
        color: 'text-orange-500',
        explanation: 'Tu caso presenta obstáculos importantes que requerirán un enfoque especializado y paciencia excepcional. La reconquista es posible, pero necesitarás implementar estrategias avanzadas y estar preparada para un proceso más largo.',
        recommendations: ['Implementa el Protocolo Extendido de Transformación (60 días)', 'Mantén silencio completo durante 21-30 días mientras trabajas en tu desarrollo personal', 'Enfócate en crear una imagen completamente renovada antes de cualquier intento de reconexión', 'Utiliza técnicas de influencia indirecta a través de círculos sociales compartidos', 'Considera la consulta personalizada con uno de nuestros especialistas para un plan a medida'],
        timeframe: '2-3 meses'
      };
    }
    setResult(resultData);
  };
  return <div className="max-w-3xl mx-auto" data-id="element-645">
      <header className="mb-8 text-center" data-id="element-646">
        <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-647">
          Test de Diagnóstico Avanzado
        </h1>
        <p className="text-gray-600" data-id="element-648">
          Descubre tu probabilidad de reconquista y recibe un plan personalizado
        </p>
      </header>

      {!testCompleted ? <div className="bg-white rounded-xl shadow-md p-6" data-id="element-649">
          <div className="mb-6" data-id="element-650">
            <div className="flex justify-between text-sm text-gray-500 mb-2" data-id="element-651">
              <span data-id="element-652">
                Pregunta {currentQuestion + 1} de {questions.length}
              </span>
              <span data-id="element-653">
                {Math.round((currentQuestion + 1) / questions.length * 100)}%
                completado
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-654">
              <div className="bg-gradient-to-r from-[#E91E63] to-[#9C27B0] h-2 rounded-full" style={{
            width: `${(currentQuestion + 1) / questions.length * 100}%`
          }} data-id="element-655"></div>
            </div>
          </div>

          <div className="mb-4 flex items-start justify-between" data-id="element-656">
            <h2 className="text-xl font-semibold text-[#2C2C2C]" data-id="element-657">
              {questions[currentQuestion].question}
            </h2>
            <button onClick={() => setShowExplanation(!showExplanation)} className="ml-2 p-1 text-gray-500 hover:text-[#9C27B0] focus:outline-none" data-id="element-658">
              <InfoIcon size={20} data-id="element-659" />
            </button>
          </div>

          {showExplanation && <div className="mb-6 bg-purple-50 border-l-4 border-purple-300 p-4" data-id="element-660">
              <div className="flex" data-id="element-661">
                <div className="flex-shrink-0" data-id="element-662">
                  <InfoIcon size={20} className="text-purple-600" data-id="element-663" />
                </div>
                <div className="ml-3" data-id="element-664">
                  <p className="text-sm text-purple-700" data-id="element-665">
                    {questions[currentQuestion].explanation}
                  </p>
                  <p className="text-sm font-medium text-purple-800 mt-2" data-id="element-666">
                    Impacto en el resultado: {questions[currentQuestion].impact}
                  </p>
                </div>
              </div>
            </div>}

          <div className="space-y-3 mb-8" data-id="element-667">
            {questions[currentQuestion].options.map((option, index) => <div key={index} className={`p-4 border rounded-lg cursor-pointer transition-all ${answers[currentQuestion] === index ? 'border-[#E91E63] bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => handleAnswerSelect(currentQuestion, index)} data-id="element-668">
                <div className="flex items-center" data-id="element-669">
                  <div className={`w-5 h-5 rounded-full border mr-3 flex items-center justify-center ${answers[currentQuestion] === index ? 'border-[#E91E63] bg-[#E91E63]' : 'border-gray-300'}`} data-id="element-670">
                    {answers[currentQuestion] === index && <CheckCircleIcon size={14} className="text-white" data-id="element-671" />}
                  </div>
                  <span data-id="element-672">{option}</span>
                </div>
              </div>)}
          </div>

          <div className="flex justify-between" data-id="element-673">
            <button onClick={goToPreviousQuestion} disabled={currentQuestion === 0} className={`flex items-center px-4 py-2 rounded-md ${currentQuestion === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-674">
              <ArrowLeftIcon size={16} className="mr-2" data-id="element-675" />
              Anterior
            </button>
            <button onClick={goToNextQuestion} disabled={answers[currentQuestion] === undefined} className={`flex items-center px-4 py-2 rounded-md ${answers[currentQuestion] === undefined ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#E91E63] text-white hover:bg-[#D81B60]'}`} data-id="element-676">
              {currentQuestion < questions.length - 1 ? 'Siguiente' : 'Finalizar'}
              <ArrowRightIcon size={16} className="ml-2" data-id="element-677" />
            </button>
          </div>
        </div> : <div className="bg-white rounded-xl shadow-md p-6 text-center" data-id="element-678">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-679">
            <CheckCircleIcon size={48} className="text-green-500" data-id="element-680" />
          </div>
          <h2 className="text-2xl font-bold text-[#2C2C2C] mb-2" data-id="element-681">
            ¡Diagnóstico Completo!
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-682">
            Basado en tus respuestas, hemos analizado tu situación específica y
            calculado tu probabilidad de éxito.
          </p>

          <div className="mb-8" data-id="element-683">
            <div className="text-5xl font-bold mb-2 text-center" data-id="element-684">
              <span className={result.color} data-id="element-685">{result.percentage}%</span>
            </div>
            <p className="text-gray-600" data-id="element-686">Probabilidad de reconquista exitosa</p>
            <div className="mt-2 inline-block px-3 py-1 rounded-full bg-gray-100 text-gray-700" data-id="element-687">
              Categoría: <span className="font-medium" data-id="element-688">{result.category}</span>
            </div>
          </div>

          <div className="bg-gray-50 border-l-4 border-[#9C27B0] p-4 text-left mb-8" data-id="element-689">
            <h3 className="font-bold text-[#2C2C2C] mb-2" data-id="element-690">
              Análisis Personalizado:
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-691">{result.explanation}</p>
            <h4 className="font-semibold text-[#2C2C2C] mt-4 mb-2" data-id="element-692">
              Recomendaciones Estratégicas:
            </h4>
            <ul className="space-y-2" data-id="element-693">
              {result.recommendations.map((rec, index) => <li key={index} className="flex items-start" data-id="element-694">
                  <CheckCircleIcon size={16} className="text-[#9C27B0] mr-2 mt-0.5 flex-shrink-0" data-id="element-695" />
                  <span className="text-gray-700" data-id="element-696">{rec}</span>
                </li>)}
            </ul>
            <div className="mt-4 p-3 bg-pink-50 rounded-lg" data-id="element-697">
              <div className="flex items-center" data-id="element-698">
                <div className="mr-3" data-id="element-699">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-pink-600" viewBox="0 0 20 20" fill="currentColor" data-id="element-700">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" data-id="element-701" />
                  </svg>
                </div>
                <div data-id="element-702">
                  <p className="text-sm font-medium text-pink-700" data-id="element-703">
                    Tiempo estimado para resultados visibles
                  </p>
                  <p className="text-sm text-pink-600" data-id="element-704">{result.timeframe}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8 bg-gray-50 rounded-lg p-6" data-id="element-705">
            <h3 className="font-bold text-[#2C2C2C] mb-4 text-left" data-id="element-706">
              Tu Plan de Acción Personalizado:
            </h3>
            <div className="space-y-4" data-id="element-707">
              <div className="flex items-start" data-id="element-708">
                <div className="bg-[#E91E63] rounded-full w-8 h-8 flex items-center justify-center text-white font-bold mr-3 flex-shrink-0" data-id="element-709">
                  1
                </div>
                <div className="text-left" data-id="element-710">
                  <h4 className="font-semibold text-[#2C2C2C]" data-id="element-711">
                    Preparación Mental
                  </h4>
                  <p className="text-sm text-gray-700 mt-1" data-id="element-712">
                    Comienza con los ejercicios de fortalecimiento emocional en
                    la sección de Crecimiento Personal. Completa al menos 3
                    ejercicios antes de iniciar cualquier contacto.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-713">
                <div className="bg-[#E91E63] rounded-full w-8 h-8 flex items-center justify-center text-white font-bold mr-3 flex-shrink-0" data-id="element-714">
                  2
                </div>
                <div className="text-left" data-id="element-715">
                  <h4 className="font-semibold text-[#2C2C2C]" data-id="element-716">
                    Identificación de Tipo Masculino
                  </h4>
                  <p className="text-sm text-gray-700 mt-1" data-id="element-717">
                    Completa el análisis de Tipo de Hombre para personalizar tus
                    mensajes y enfoque según su personalidad específica.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-718">
                <div className="bg-[#E91E63] rounded-full w-8 h-8 flex items-center justify-center text-white font-bold mr-3 flex-shrink-0" data-id="element-719">
                  3
                </div>
                <div className="text-left" data-id="element-720">
                  <h4 className="font-semibold text-[#2C2C2C]" data-id="element-721">
                    Primer Contacto Estratégico
                  </h4>
                  <p className="text-sm text-gray-700 mt-1" data-id="element-722">
                    Utiliza el mensaje recomendado de la Biblioteca de Mensajes,
                    personalizado según tu situación específica. Momento óptimo:{' '}
                    {result.percentage >= 70 ? 'En los próximos 2-3 días' : 'Después de 7-10 días de preparación'}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-723">
                <div className="bg-[#E91E63] rounded-full w-8 h-8 flex items-center justify-center text-white font-bold mr-3 flex-shrink-0" data-id="element-724">
                  4
                </div>
                <div className="text-left" data-id="element-725">
                  <h4 className="font-semibold text-[#2C2C2C]" data-id="element-726">
                    Seguimiento del Plan de 21 Días
                  </h4>
                  <p className="text-sm text-gray-700 mt-1" data-id="element-727">
                    Sigue el calendario personalizado con los mensajes y
                    acciones recomendadas para cada día, adaptado a tu situación
                    específica y a sus respuestas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-center gap-4" data-id="element-728">
            <button onClick={() => window.location.href = '/'} className="bg-[#E91E63] text-white px-6 py-3 rounded-md hover:bg-[#D81B60] w-full md:w-auto" data-id="element-729">
              Ver Mi Plan Completo
            </button>
            <button onClick={() => {
          setTestCompleted(false);
          setCurrentQuestion(0);
          setAnswers({});
        }} className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-50 w-full md:w-auto" data-id="element-730">
              Rehacer Test
            </button>
            <button onClick={() => window.location.href = '/male-type'} className="bg-[#9C27B0] text-white px-6 py-3 rounded-md hover:bg-[#7B1FA2] w-full md:w-auto" data-id="element-731">
              Identificar su Tipo
            </button>
          </div>
        </div>}
    </div>;
};
export default DiagnosticTest;